import React, { useState } from "react";
import { Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { MapPin, Search, CheckCircle2, HelpCircle, CalendarHeart, Loader2 } from "lucide-react";

const CONTAINER = "max-w-3xl mx-auto px-4 sm:px-6";

export function CoverageChecker({ franchise, cityId, label }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await publicApi.post("/public/local/coverage", {
        franchise: franchise || undefined, city_id: cityId || undefined, query: query.trim(),
      });
      setResult(data);
    } catch {
      setResult({ matched: false, cta_href: "/book",
        message: "We may still be able to help. Book a free consultation and we'll confirm service availability." });
    } finally { setLoading(false); }
  };

  return (
    <section className="py-14 sm:py-16 bg-[#F4F8FB]" data-testid="coverage-checker">
      <div className={CONTAINER}>
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">
            <MapPin size={15} /> Service area
          </span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">Is your area covered?</h2>
          <p className="mt-2 text-[16px] text-[#4B5563]">
            Check if {label ? `our ${label} team` : "we"} serve your neighbourhood — enter a city or ZIP / postal code.
          </p>
        </div>
        <form onSubmit={check} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <input
            data-testid="coverage-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. 64105 or Kansas City"
            className="flex-1 h-13 sm:h-14 rounded-full border border-[#1C4358]/20 bg-white px-6 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#1C4358]/20" />
          <button type="submit" data-testid="coverage-submit" disabled={loading}
            className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-13 sm:h-14 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163646] transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} Check
          </button>
        </form>
        {result && (
          <div data-testid="coverage-result"
            className={`mt-6 max-w-xl mx-auto rounded-2xl p-5 flex items-start gap-3 ${result.matched ? "bg-[#2F7D5A]/10" : "bg-white border border-[#1C4358]/12"}`}>
            {result.matched
              ? <CheckCircle2 className="text-[#2F7D5A] shrink-0 mt-0.5" size={22} data-testid="coverage-matched" />
              : <HelpCircle className="text-[#B7791F] shrink-0 mt-0.5" size={22} data-testid="coverage-soft" />}
            <div>
              <p className="text-[16px] text-[#252221] leading-relaxed">{result.message}</p>
              <Link to={result.cta_href || "/book"} data-testid="coverage-cta"
                className="mt-3 inline-flex items-center gap-2 px-6 h-11 rounded-full text-[15px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors">
                <CalendarHeart size={17} /> Book Free Consultation
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default CoverageChecker;
